import { db } from '../../utils/firebase';
import { collection, addDoc, getDocs, getDoc, query, where, doc, setDoc, updateDoc, deleteDoc } from '../../utils/firebase';
import { writeBatch, Timestamp } from 'firebase/firestore';
import { buildFirestorePath } from '../../utils/settings/settingsConfig';

// Event tracking for behavior analytics
import { trackAction, ACTIONS } from '../../utils/useEventTracker';

/**
 * Allocation Service
 * Handles all allocation-related operations including CRUD, conflict detection,
 * recurring allocations, and capacity calculations
 * 
 * UPDATED: Uses flattened allocation path (clubs/{clubId}/allocations/)
 * UPDATED: Requires facilityId on all allocations
 */

// ==================== PITCH NAMING HELPERS ====================

/**
 * Normalize pitch ID to consistent format: pitch-{number}
 */
const normalizePitchId = (pitchId) => {
  if (!pitchId) return '';
  const id = String(pitchId);
  if (id.startsWith('pitch-')) return id;
  if (id.startsWith('pitch')) return id.replace('pitch', 'pitch-');
  return `pitch-${id}`;
};

// ==================== HELPER FUNCTIONS ====================

const toTimestamp = (dateString) => {
  const date = new Date(dateString);
  return Timestamp.fromDate(date);
};

const toDateString = (timestamp) => {
  if (!timestamp) return null;
  const date = timestamp.toDate();
  return date.toISOString().split('T')[0];
};

/**
 * Generate allocation key for Firestore document ID
 * Format: date-time-pitchId-section (e.g., "2025-10-27-17:00-pitch-1-A")
 */
const generateAllocationKey = (date, startTime, pitchId, section = '') => {
  const sectionPart = section ? `-${section}` : '';
  return `${date}-${startTime}-${pitchId}${sectionPart}`;
};

/**
 * Parse allocation key to extract components
 * Key format: YYYY-MM-DD-HH:MM-pitch-N-SECTION
 * Example: 2025-12-09-17:00-pitch-4-A
 */
const parseAllocationKey = (key) => {
  const parts = key.split('-');
  // Parts: ['2025', '12', '09', '17:00', 'pitch', '4', 'A']
  //         0       1     2     3        4        5    6
  if (parts.length < 6) {
    console.warn('Invalid allocation key format:', key);
    return null;
  }
  return {
    date: `${parts[0]}-${parts[1]}-${parts[2]}`,
    startTime: parts[3], // Already in HH:MM format
    pitchId: `${parts[4]}-${parts[5]}`, // Reconstruct pitch-N
    section: parts[6] || ''
  };
};

/**
 * Extract pitchId from allocation key
 * Key format: YYYY-MM-DD-HH:MM-pitch-N-SECTION
 */
const extractPitchIdFromKey = (allocationKey) => {
  const parsed = parseAllocationKey(allocationKey);
  return parsed ? parsed.pitchId : null;
};

const timesOverlap = (start1, end1, start2, end2) => {
  return start1 < end2 && start2 < end1;
};

const calculateDuration = (startTime, endTime) => {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  return endTotalMinutes - startTotalMinutes;
};

const isDateInRange = (date, startDate, endDate) => {
  return date >= startDate && date <= endDate;
};

const getDayOfWeek = (dateString) => {
  return new Date(dateString).getDay();
};

// ==================== ALLOCATION CRUD ====================

/**
 * Create a new allocation
 * UPDATED: Now requires facilityId and uses flattened path
 */
export const createAllocation = async (clubId, allocationData) => {
  try {
    const {
      facilityId,           // NEW: Required
      facilityHostClubId,   // NEW: Optional - only for shared facilities
      pitchId: inputPitchId,
      pitchName,
      sections,
      date,
      startTime,
      endTime,
      duration,
      teamId,
      teamName,
      allocationType,
      isRecurring = false,
      recurrencePattern = null,
      notes = ''
    } = allocationData;

    console.log('🔍 Creating allocation with clubId:', clubId);
    console.log('🔍 Allocation data:', allocationData);

    // Validate basic required fields first
    if (!inputPitchId || !date || !startTime || !endTime || !teamId) {
      throw new Error('Missing required allocation fields');
    }

    if (!clubId || clubId.length !== 6) {
      throw new Error(`Invalid clubId format: ${clubId}. Must be 6 characters.`);
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new Error(`Invalid date format: ${date}. Must be YYYY-MM-DD.`);
    }

    // Handle facilityId - get from primary if not provided
    let resolvedFacilityId = facilityId;
    if (!resolvedFacilityId) {
      console.log('⚠️ No facilityId provided, attempting to get primary facility...');
      try {
        const clubRef = doc(db, 'clubs', clubId);
        const clubDoc = await getDoc(clubRef);
        if (clubDoc.exists()) {
          resolvedFacilityId = clubDoc.data().primaryFacilityId || null;
          console.log('🏟️ Resolved to primary facility:', resolvedFacilityId);
        }
      } catch (e) {
        console.warn('Could not resolve primary facility:', e);
      }
    }

    // Allow legacy mode (no facility) if no facilities exist
    if (!resolvedFacilityId) {
      console.log('⚠️ No facility found - operating in legacy mode (no facility)');
    }

    // Validate allocation doesn't extend beyond facility operating hours
    if (resolvedFacilityId && endTime) {
      try {
        const facilityRef = doc(db, buildFirestorePath.facilityDoc(clubId, resolvedFacilityId));
        const facilityDoc = await getDoc(facilityRef);
        if (facilityDoc.exists()) {
          const facilityData = facilityDoc.data();
          const facilityEndTime = facilityData.endTime;
          if (facilityEndTime && endTime > facilityEndTime) {
            console.log(`🚫 Allocation end time (${endTime}) exceeds facility closing time (${facilityEndTime})`);
            return {
              success: false,
              error: `Allocation cannot extend beyond facility operating hours (closes at ${facilityEndTime})`,
              exceedsOperatingHours: true
            };
          }
        }
      } catch (e) {
        console.warn('Could not validate against facility operating hours:', e);
        // Continue anyway - don't block if we can't fetch facility data
      }
    }

    const pitchId = normalizePitchId(inputPitchId);
    if (!pitchId || !pitchId.match(/^pitch-\d+$/)) {
      throw new Error(`Invalid pitchId format: ${inputPitchId}. Must be a valid pitch identifier.`);
    }
    
    console.log('✅ Using normalized pitchId:', pitchId);
    console.log('✅ Using facilityId:', resolvedFacilityId || 'NONE (legacy mode)');

    if (allocationType && !['training', 'game', 'match'].includes(allocationType)) {
      console.warn(`⚠️ Invalid allocationType: ${allocationType}. Defaulting to 'training'.`);
    }
    const validAllocationType = ['training', 'game', 'match'].includes(allocationType) ? allocationType : 'training';

    const validSections = sections && sections.length > 0 
      ? sections.filter(s => s && s.trim() !== '') 
      : [];
    
    if (validSections.length === 0) {
      throw new Error('At least one section must be selected');
    }

    // Check for conflicts - now scoped to facility
    const conflicts = await checkAllocationConflicts(clubId, {
      facilityId: resolvedFacilityId,
      pitchId,
      sections: validSections,
      date,
      startTime,
      endTime
    });

    if (conflicts.length > 0) {
      return {
        success: false,
        error: 'Allocation conflicts with existing bookings',
        conflicts
      };
    }

    const allocationDuration = duration || calculateDuration(startTime, endTime);
    const timeSlots = [];
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    let currentMinutes = startHours * 60 + startMinutes;
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    while (currentMinutes < endTotalMinutes) {
      const hours = Math.floor(currentMinutes / 60);
      const minutes = currentMinutes % 60;
      const timeSlot = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      timeSlots.push(timeSlot);
      currentMinutes += 15;
    }

    const allocationKeys = [];
    
    for (const section of validSections) {
      for (const timeSlot of timeSlots) {
        const allocationKey = generateAllocationKey(date, timeSlot, pitchId, section);
        allocationKeys.push(allocationKey);
        
        const allocation = {
          facilityId: resolvedFacilityId,  // Resolved facility ID (or null for legacy)
          facilityHostClubId: facilityHostClubId || null, // NEW: For shared facilities
          team: teamName,
          teamName,
          pitch: pitchId,
          pitchId,
          pitchName,
          section,
          sections: [section],
          date,
          startTime,
          endTime,
          timeSlot,
          duration: allocationDuration,
          teamId,
          clubId,
          type: validAllocationType,
          allocationType: validAllocationType,
          isRecurring,
          recurrencePattern,
          notes,
          created: Date.now(),
          createdBy: 'system',
          lastUpdated: new Date().toISOString(),
          updatedBy: 'system'
        };

        console.log('📝 Attempting to write allocation:');
        console.log('   - Path: clubs/' + clubId + '/allocations/' + pitchId + '/' + allocationKey);
        console.log('   - facilityId:', resolvedFacilityId || 'NONE (legacy)');

        // Save to pitch-based allocations subcollection
        const allocationRef = doc(db, buildFirestorePath.allocationDoc(clubId, pitchId, allocationKey));
        await setDoc(allocationRef, allocation);
      }
    }

    if (isRecurring && recurrencePattern) {
      await createRecurringAllocations(clubId, {
        ...allocationData,
        facilityId: resolvedFacilityId,
        sections: validSections,
        parentAllocationKeys: allocationKeys
      });
    }

    // Track allocation creation for behavior analytics
    trackAction(ACTIONS.ALLOCATION_CREATED, { 
      clubId, 
      facilityId: resolvedFacilityId,
      teamId, 
      pitchId, 
      isRecurring,
      sectionsCount: validSections.length
    });

    return {
      success: true,
      allocationKeys,
      allocation: validSections[0]
    };
  } catch (error) {
    if (error.message === 'TRIAL_EXPIRED') {
      console.log('🚫 Trial expired - allocation blocked');
      return {
        success: false,
        error: 'TRIAL_EXPIRED',
        blocked: true
      };
    }
    
    console.error('Error creating allocation:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get a single allocation
 * Uses pitch-based subcollection path
 */
export const getAllocation = async (clubId, allocationKey) => {
  try {
    const pitchId = extractPitchIdFromKey(allocationKey);
    if (!pitchId) {
      console.error('Could not extract pitchId from allocation key:', allocationKey);
      return null;
    }
    
    const allocationRef = doc(db, buildFirestorePath.allocationDoc(clubId, pitchId, allocationKey));
    const allocationDoc = await getDoc(allocationRef);

    if (!allocationDoc.exists()) {
      return null;
    }

    return {
      id: allocationDoc.id,
      ...allocationDoc.data()
    };
  } catch (error) {
    console.error('Error getting allocation:', error);
    return null;
  }
};

/**
 * Get all allocations for a facility and pitch on a specific date
 * Uses pitch-based subcollection for efficient querying
 */
export const getAllocationsForPitchAndDate = async (clubId, pitchId, date, facilityId = null) => {
  try {
    console.log('🔍 Loading allocations for:', { clubId, pitchId, date, facilityId });
    
    // Query directly from the pitch's subcollection
    const allocationsRef = collection(db, buildFirestorePath.allocationsCollection(clubId, pitchId));
    
    // Build query - no need to filter by pitchId since we're in that pitch's collection
    let q;
    if (facilityId) {
      q = query(
        allocationsRef,
        where('facilityId', '==', facilityId),
        where('date', '==', date)
      );
    } else {
      q = query(
        allocationsRef,
        where('date', '==', date)
      );
    }

    const querySnapshot = await getDocs(q);
    const allocations = [];

    querySnapshot.forEach((doc) => {
      allocations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`✅ Loaded ${allocations.length} allocations for ${pitchId} on ${date}`);

    allocations.sort((a, b) => a.startTime.localeCompare(b.startTime));

    return allocations;
  } catch (error) {
    console.error('Error getting allocations for pitch and date:', error);
    return [];
  }
};

/**
 * Get all allocations for a facility on a specific date
 * Queries each pitch subcollection and combines results
 */
export const getAllocationsForFacilityAndDate = async (clubId, facilityId, date) => {
  try {
    console.log('🔍 Loading allocations for facility:', { clubId, facilityId, date });
    
    // First, get the facility to find its pitches
    const facilityRef = doc(db, buildFirestorePath.facilityDoc(clubId, facilityId));
    const facilityDoc = await getDoc(facilityRef);
    
    if (!facilityDoc.exists()) {
      console.error('Facility not found:', facilityId);
      return [];
    }
    
    const facilityData = facilityDoc.data();
    const pitches = facilityData.pitches || [];
    
    if (pitches.length === 0) {
      console.log('No pitches in facility');
      return [];
    }
    
    // Query each pitch's subcollection in parallel
    const allocationPromises = pitches.map(async (pitch) => {
      const pitchId = pitch.id || `pitch-${pitch.pitchNumber}`;
      const allocationsRef = collection(db, buildFirestorePath.allocationsCollection(clubId, pitchId));
      const q = query(
        allocationsRef,
        where('date', '==', date)
      );
      
      const snapshot = await getDocs(q);
      const results = [];
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
      return results;
    });
    
    const allResults = await Promise.all(allocationPromises);
    const allocations = allResults.flat();

    console.log(`✅ Loaded ${allocations.length} allocations for facility ${facilityId} on ${date}`);

    allocations.sort((a, b) => {
      const pitchCompare = (a.pitchId || '').localeCompare(b.pitchId || '');
      if (pitchCompare !== 0) return pitchCompare;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });

    return allocations;
  } catch (error) {
    console.error('Error getting allocations for facility and date:', error);
    return [];
  }
};

/**
 * Get all allocations for a club within a date range
 * Queries across all pitches in specified facility (or all facilities)
 */
export const getAllocationsForDateRange = async (clubId, startDate, endDate, facilityId = null) => {
  try {
    // Get facilities to find all pitches
    const facilitiesRef = collection(db, buildFirestorePath.facilitiesCollection(clubId));
    const facilitiesSnap = await getDocs(facilitiesRef);
    
    const facilities = [];
    facilitiesSnap.forEach((doc) => {
      if (!facilityId || doc.id === facilityId) {
        facilities.push({ id: doc.id, ...doc.data() });
      }
    });
    
    if (facilities.length === 0) {
      return [];
    }
    
    // Collect all pitches from matching facilities
    const allPitches = [];
    facilities.forEach((facility) => {
      const pitches = facility.pitches || [];
      pitches.forEach((pitch) => {
        const pitchId = pitch.id || `pitch-${pitch.pitchNumber}`;
        allPitches.push({ pitchId, facilityId: facility.id });
      });
    });
    
    if (allPitches.length === 0) {
      return [];
    }
    
    // Query each pitch subcollection in parallel
    const allocationPromises = allPitches.map(async ({ pitchId }) => {
      const allocationsRef = collection(db, buildFirestorePath.allocationsCollection(clubId, pitchId));
      const q = query(
        allocationsRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );
      
      const snapshot = await getDocs(q);
      const results = [];
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
      return results;
    });
    
    const allResults = await Promise.all(allocationPromises);
    const allAllocations = allResults.flat();

    allAllocations.sort((a, b) => {
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });

    return allAllocations;
  } catch (error) {
    console.error('Error getting allocations for date range:', error);
    return [];
  }
};

/**
 * Update an allocation
 * Uses pitch-based subcollection path
 */
export const updateAllocation = async (clubId, allocationKey, updates) => {
  try {
    const pitchId = extractPitchIdFromKey(allocationKey);
    if (!pitchId) {
      return { success: false, error: 'Could not extract pitchId from allocation key' };
    }
    
    const allocationRef = doc(db, buildFirestorePath.allocationDoc(clubId, pitchId, allocationKey));
    
    const allocationDoc = await getDoc(allocationRef);
    if (!allocationDoc.exists()) {
      return {
        success: false,
        error: 'Allocation not found'
      };
    }

    const currentData = allocationDoc.data();

    if (updates.startTime || updates.endTime || updates.sections) {
      const conflicts = await checkAllocationConflicts(clubId, {
        facilityId: currentData.facilityId,
        pitchId: currentData.pitchId,
        sections: updates.sections || currentData.sections,
        date: currentData.date,
        startTime: updates.startTime || currentData.startTime,
        endTime: updates.endTime || currentData.endTime,
        excludeAllocationKey: allocationKey
      });

      if (conflicts.length > 0) {
        return {
          success: false,
          error: 'Update would create conflicts',
          conflicts
        };
      }
    }

    if (updates.startTime || updates.endTime) {
      const newStartTime = updates.startTime || currentData.startTime;
      const newEndTime = updates.endTime || currentData.endTime;
      updates.duration = calculateDuration(newStartTime, newEndTime);
    }

    updates.lastUpdated = new Date().toISOString();
    updates.updatedBy = 'system';

    await updateDoc(allocationRef, updates);

    // Track allocation edit for behavior analytics
    trackAction(ACTIONS.ALLOCATION_EDITED, { clubId, pitchId, allocationKey });

    return {
      success: true,
      allocationKey
    };
  } catch (error) {
    console.error('Error updating allocation:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete an allocation
 * Uses pitch-based subcollection path
 */
export const deleteAllocation = async (clubId, allocationKey) => {
  try {
    const pitchId = extractPitchIdFromKey(allocationKey);
    if (!pitchId) {
      return { success: false, error: 'Could not extract pitchId from allocation key' };
    }
    
    const allocationRef = doc(db, buildFirestorePath.allocationDoc(clubId, pitchId, allocationKey));
    
    const allocationDoc = await getDoc(allocationRef);
    if (!allocationDoc.exists()) {
      return {
        success: false,
        error: 'Allocation not found'
      };
    }

    const allocationData = allocationDoc.data();
    
    if (allocationData.isRecurring && !allocationData.parentAllocationKeys) {
      return await deleteRecurringAllocations(clubId, allocationData.facilityId, allocationKey);
    }

    await deleteDoc(allocationRef);

    // Track allocation deletion for behavior analytics
    trackAction(ACTIONS.ALLOCATION_DELETED, { 
      clubId, 
      pitchId,
      facilityId: allocationData.facilityId
    });

    return {
      success: true,
      deletedCount: 1
    };
  } catch (error) {
    console.error('Error deleting allocation:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete all allocations for a team
 * Queries across all pitches to find team's allocations
 */
export const deleteTeamAllocations = async (clubId, teamId) => {
  try {
    // Get all facilities to find all pitches
    const facilitiesRef = collection(db, buildFirestorePath.facilitiesCollection(clubId));
    const facilitiesSnap = await getDocs(facilitiesRef);
    
    const allPitchIds = [];
    facilitiesSnap.forEach((facilityDoc) => {
      const pitches = facilityDoc.data().pitches || [];
      pitches.forEach((pitch) => {
        const pitchId = pitch.id || `pitch-${pitch.pitchNumber}`;
        allPitchIds.push(pitchId);
      });
    });
    
    let totalDeleted = 0;
    
    // Delete from each pitch subcollection
    for (const pitchId of allPitchIds) {
      const allocationsRef = collection(db, buildFirestorePath.allocationsCollection(clubId, pitchId));
      const q = query(allocationsRef, where('teamId', '==', teamId));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.size > 0) {
        const batch = writeBatch(db);
        querySnapshot.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        totalDeleted += querySnapshot.size;
      }
    }

    return {
      success: true,
      deletedCount: totalDeleted
    };
  } catch (error) {
    console.error('Error deleting team allocations:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete all allocations for a facility
 * Deletes from each pitch subcollection in the facility
 */
export const deleteFacilityAllocations = async (clubId, facilityId) => {
  try {
    // Get the facility to find its pitches
    const facilityRef = doc(db, buildFirestorePath.facilityDoc(clubId, facilityId));
    const facilityDoc = await getDoc(facilityRef);
    
    if (!facilityDoc.exists()) {
      return { success: false, error: 'Facility not found' };
    }
    
    const pitches = facilityDoc.data().pitches || [];
    let totalDeleted = 0;
    
    // Delete from each pitch subcollection
    for (const pitch of pitches) {
      const pitchId = pitch.id || `pitch-${pitch.pitchNumber}`;
      const allocationsRef = collection(db, buildFirestorePath.allocationsCollection(clubId, pitchId));
      const querySnapshot = await getDocs(allocationsRef);
      
      if (querySnapshot.size > 0) {
        const batch = writeBatch(db);
        querySnapshot.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        totalDeleted += querySnapshot.size;
      }
    }

    return {
      success: true,
      deletedCount: totalDeleted
    };
  } catch (error) {
    console.error('Error deleting facility allocations:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ==================== CONFLICT DETECTION ====================

/**
 * Check for allocation conflicts
 * UPDATED: Now scoped to facilityId
 */
export const checkAllocationConflicts = async (clubId, allocationData) => {
  try {
    const {
      facilityId,
      pitchId,
      sections,
      date,
      startTime,
      endTime,
      excludeAllocationKey = null
    } = allocationData;

    // Get existing allocations for this facility, pitch and date
    const existingAllocations = await getAllocationsForPitchAndDate(clubId, pitchId, date, facilityId);

    const conflicts = [];

    for (const existing of existingAllocations) {
      if (excludeAllocationKey && existing.id === excludeAllocationKey) {
        continue;
      }

      if (!timesOverlap(startTime, endTime, existing.startTime, existing.endTime)) {
        continue;
      }

      const sectionsOverlap = sections.length === 0 || existing.sections.length === 0 ||
        sections.some(section => existing.sections.includes(section));

      if (sectionsOverlap) {
        conflicts.push({
          allocationKey: existing.id,
          teamName: existing.teamName,
          startTime: existing.startTime,
          endTime: existing.endTime,
          sections: existing.sections
        });
      }
    }

    return conflicts;
  } catch (error) {
    console.error('Error checking allocation conflicts:', error);
    return [];
  }
};

/**
 * Check for conflicts with partner club (for shared facilities)
 * NEW: Cross-club conflict detection
 */
export const checkSharedFacilityConflicts = async (clubId, partnerClubId, allocationData) => {
  try {
    const {
      facilityId,
      pitchId,
      sections,
      date,
      startTime,
      endTime
    } = allocationData;

    // Get both club's allocations
    const ownConflicts = await checkAllocationConflicts(clubId, allocationData);
    const partnerConflicts = await checkAllocationConflicts(partnerClubId, {
      ...allocationData,
      facilityId // Partner uses same facilityId
    });

    return {
      ownConflicts,
      partnerConflicts,
      hasConflicts: ownConflicts.length > 0 || partnerConflicts.length > 0
    };
  } catch (error) {
    console.error('Error checking shared facility conflicts:', error);
    return {
      ownConflicts: [],
      partnerConflicts: [],
      hasConflicts: false
    };
  }
};

// ==================== RECURRING ALLOCATIONS ====================

/**
 * Create recurring allocations based on pattern
 * UPDATED: Uses flattened path and requires facilityId
 */
export const createRecurringAllocations = async (clubId, allocationData) => {
  try {
    const {
      facilityId,
      facilityHostClubId,
      pitchId,
      pitchName,
      sections,
      date,
      startTime,
      endTime,
      duration,
      teamId,
      teamName,
      allocationType,
      recurrencePattern,
      notes,
      parentAllocationKeys
    } = allocationData;

    const {
      frequency,
      daysOfWeek,
      endDate
    } = recurrencePattern;

    const validSections = sections && sections.length > 0 
      ? sections.filter(s => s && s.trim() !== '') 
      : [];
    
    if (validSections.length === 0) {
      throw new Error('At least one section must be selected for recurring allocations');
    }

    // Validate allocation doesn't extend beyond facility operating hours
    if (facilityId && endTime) {
      try {
        const facilityRef = doc(db, buildFirestorePath.facilityDoc(clubId, facilityId));
        const facilityDoc = await getDoc(facilityRef);
        if (facilityDoc.exists()) {
          const facilityData = facilityDoc.data();
          const facilityEndTime = facilityData.endTime;
          if (facilityEndTime && endTime > facilityEndTime) {
            console.log(`🚫 Recurring allocation end time (${endTime}) exceeds facility closing time (${facilityEndTime})`);
            return {
              success: false,
              error: `Allocation cannot extend beyond facility operating hours (closes at ${facilityEndTime})`,
              exceedsOperatingHours: true
            };
          }
        }
      } catch (e) {
        console.warn('Could not validate against facility operating hours:', e);
      }
    }

    const allocationDuration = duration || calculateDuration(startTime, endTime);
    const timeSlots = [];
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    let currentMinutes = startHours * 60 + startMinutes;
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    while (currentMinutes < endTotalMinutes) {
      const hours = Math.floor(currentMinutes / 60);
      const minutes = currentMinutes % 60;
      const timeSlot = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      timeSlots.push(timeSlot);
      currentMinutes += 15;
    }

    const allocations = [];
    const currentDate = new Date(date);
    const finalDate = new Date(endDate);

    let weekIncrement = frequency === 'weekly' ? 1 : 2;

    while (currentDate <= finalDate) {
      const dateString = currentDate.toISOString().split('T')[0];
      const dayOfWeek = currentDate.getDay();

      if (daysOfWeek.includes(dayOfWeek)) {
        if (dateString !== date) {
          const conflicts = await checkAllocationConflicts(clubId, {
            facilityId,
            pitchId,
            sections: validSections,
            date: dateString,
            startTime,
            endTime
          });

          if (conflicts.length === 0) {
            for (const section of validSections) {
              for (const timeSlot of timeSlots) {
                const allocationKey = generateAllocationKey(dateString, timeSlot, pitchId, section);
                
                const allocation = {
                  facilityId,
                  facilityHostClubId: facilityHostClubId || null,
                  team: teamName,
                  teamName,
                  pitch: pitchId,
                  pitchId,
                  pitchName,
                  section,
                  sections: [section],
                  date: dateString,
                  startTime,
                  endTime,
                  timeSlot,
                  duration: allocationDuration,
                  teamId,
                  clubId,
                  type: allocationType,
                  allocationType,
                  isRecurring: true,
                  recurrencePattern,
                  parentAllocationKeys,
                  notes,
                  created: Date.now(),
                  createdBy: 'system',
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'system'
                };

                allocations.push({
                  key: allocationKey,
                  data: allocation
                });
              }
            }
          }
        }
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    const batch = writeBatch(db);
    
    for (const allocation of allocations) {
      const pitchIdForPath = allocation.data.pitchId;
      const allocationRef = doc(db, buildFirestorePath.allocationDoc(clubId, pitchIdForPath, allocation.key));
      batch.set(allocationRef, allocation.data);
    }

    await batch.commit();

    return {
      success: true,
      createdCount: allocations.length
    };
  } catch (error) {
    console.error('Error creating recurring allocations:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete all instances of a recurring allocation
 * Uses pitch-based subcollection path
 */
export const deleteRecurringAllocations = async (clubId, facilityId, parentAllocationKey) => {
  try {
    const parentPitchId = extractPitchIdFromKey(parentAllocationKey);
    if (!parentPitchId) {
      return { success: false, error: 'Could not extract pitchId from parent allocation key' };
    }
    
    // Query the pitch subcollection for allocations with this parent key
    const allocationsRef = collection(db, buildFirestorePath.allocationsCollection(clubId, parentPitchId));
    const q = query(allocationsRef, where('parentAllocationKeys', 'array-contains', parentAllocationKey));
    
    const querySnapshot = await getDocs(q);

    // Collect all refs to delete (children + parent)
    const refsToDelete = [];
    querySnapshot.forEach((doc) => {
      refsToDelete.push(doc.ref);
    });

    // Also delete the parent allocation
    const parentRef = doc(db, buildFirestorePath.allocationDoc(clubId, parentPitchId, parentAllocationKey));
    refsToDelete.push(parentRef);

    // Chunk into batches of 400 to stay well under Firestore's transaction size limit
    const BATCH_SIZE = 400;
    for (let i = 0; i < refsToDelete.length; i += BATCH_SIZE) {
      const chunk = refsToDelete.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      chunk.forEach((ref) => batch.delete(ref));
      await batch.commit();
    }

    return {
      success: true,
      deletedCount: refsToDelete.length
    };
  } catch (error) {
    console.error('Error deleting recurring allocations:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ==================== CAPACITY & ANALYTICS ====================

/**
 * Calculate pitch capacity utilization for a date range
 */
export const calculatePitchCapacity = async (clubId, facilityId, pitchId, startDate, endDate) => {
  try {
    const allocations = await getAllocationsForDateRange(clubId, startDate, endDate, facilityId);
    const pitchAllocations = allocations.filter(a => a.pitchId === pitchId);

    const daysDiff = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;
    const totalAvailableMinutes = daysDiff * 12 * 60;

    const totalAllocatedMinutes = pitchAllocations.reduce((sum, allocation) => {
      return sum + allocation.duration;
    }, 0);

    const utilizationPercentage = (totalAllocatedMinutes / totalAvailableMinutes) * 100;

    return {
      pitchId,
      facilityId,
      totalAllocations: pitchAllocations.length,
      totalAllocatedMinutes,
      totalAvailableMinutes,
      utilizationPercentage: Math.round(utilizationPercentage * 100) / 100,
      averageAllocationDuration: pitchAllocations.length > 0 
        ? Math.round(totalAllocatedMinutes / pitchAllocations.length) 
        : 0
    };
  } catch (error) {
    console.error('Error calculating pitch capacity:', error);
    return null;
  }
};

/**
 * Get allocation statistics for a team
 */
export const getTeamAllocationStats = async (clubId, teamId, startDate, endDate) => {
  try {
    const allocations = await getAllocationsForDateRange(clubId, startDate, endDate);
    const teamAllocations = allocations.filter(a => a.teamId === teamId);

    const trainingCount = teamAllocations.filter(a => a.allocationType === 'training').length;
    const matchCount = teamAllocations.filter(a => a.allocationType === 'match' || a.allocationType === 'game').length;

    const totalMinutes = teamAllocations.reduce((sum, allocation) => {
      return sum + allocation.duration;
    }, 0);

    return {
      teamId,
      totalAllocations: teamAllocations.length,
      trainingCount,
      matchCount,
      totalMinutes,
      totalHours: Math.round((totalMinutes / 60) * 100) / 100
    };
  } catch (error) {
    console.error('Error getting team allocation stats:', error);
    return null;
  }
};

/**
 * Get club-wide allocation analytics
 */
export const getClubAllocationAnalytics = async (clubId, startDate, endDate, facilityId = null) => {
  try {
    const allocations = await getAllocationsForDateRange(clubId, startDate, endDate, facilityId);

    const totalAllocations = allocations.length;
    const trainingCount = allocations.filter(a => a.allocationType === 'training').length;
    const matchCount = allocations.filter(a => a.allocationType === 'match' || a.allocationType === 'game').length;

    const totalMinutes = allocations.reduce((sum, allocation) => {
      return sum + allocation.duration;
    }, 0);

    const uniqueTeams = [...new Set(allocations.map(a => a.teamId))];

    const byDayOfWeek = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };

    allocations.forEach(allocation => {
      const day = getDayOfWeek(allocation.date);
      byDayOfWeek[day]++;
    });

    // Group by facility
    const byFacility = {};
    allocations.forEach(allocation => {
      const fId = allocation.facilityId || 'unknown';
      if (!byFacility[fId]) {
        byFacility[fId] = 0;
      }
      byFacility[fId]++;
    });

    return {
      totalAllocations,
      trainingCount,
      matchCount,
      totalMinutes,
      totalHours: Math.round((totalMinutes / 60) * 100) / 100,
      uniqueTeamsCount: uniqueTeams.length,
      averageAllocationDuration: totalAllocations > 0 ? Math.round(totalMinutes / totalAllocations) : 0,
      allocationsByDayOfWeek: byDayOfWeek,
      allocationsByFacility: byFacility
    };
  } catch (error) {
    console.error('Error getting club allocation analytics:', error);
    return null;
  }
};

// ==================== LEGACY COMPATIBILITY ====================

/**
 * Get allocation from legacy path (for migration)
 * DEPRECATED: Use getAllocation instead
 */
export const getLegacyAllocation = async (clubId, pitchId, allocationKey) => {
  try {
    const allocationRef = doc(db, 'clubs', clubId, 'pitches', pitchId, 'allocations', allocationKey);
    const allocationDoc = await getDoc(allocationRef);

    if (!allocationDoc.exists()) {
      return null;
    }

    return {
      id: allocationDoc.id,
      ...allocationDoc.data()
    };
  } catch (error) {
    console.error('Error getting legacy allocation:', error);
    return null;
  }
};

export default {
  createAllocation,
  getAllocation,
  getAllocationsForPitchAndDate,
  getAllocationsForFacilityAndDate,
  getAllocationsForDateRange,
  updateAllocation,
  deleteAllocation,
  deleteTeamAllocations,
  deleteFacilityAllocations,
  checkAllocationConflicts,
  checkSharedFacilityConflicts,
  createRecurringAllocations,
  deleteRecurringAllocations,
  calculatePitchCapacity,
  getTeamAllocationStats,
  getClubAllocationAnalytics,
  getLegacyAllocation
};
